import moment from "moment";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import FullPageLoader from "components/common/FullPageLoader";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import DateRangeFilter from "components/common/DateRangeFilter";
import { fetchDeclinedAppointments, DeclinedAppointmentsSelector } from "slices/interpreter/declinedAppointmentsSlice";

const getBackgroundStyle = (status) => {
  const map = {
    assigned:  { background: '#785c9933' },
    open:      { background: '#ffb82a33' },
    available: { background: '#ffb82a33' },
    completed: { background: '#1c2b2c33' },
    cancelled: { background: '#e33a3133' },
    declined:  { background: '#ff000033' },
    pending:   { background: '#3176a033' },
    dnc:       { background: '#3176a033' },
    cnc:       { background: '#9623e033' },
  };
  return map[status] || {};
};

const getEyeStyle = (status) => {
  const map = {
    assigned:  { background: '#785c99' },
    open:      { background: '#ffb82a' },
    available: { background: '#ffb82a' },
    completed: { background: '#1c2b2c' },
    cancelled: { background: '#e33a31' },
    declined:  { background: '#b51a12' },
    pending:   { background: '#3176a0' },
    dnc:       { background: '#3176a0' },
    cnc:       { background: '#9623e0' },
  };
  return map[status] || { background: '#888' };
};

// ─── Inner table component (uses ListLayout) ────────────────────────────────

const DeclinedListTable = ({ data, filtration }) => {
  const { total, last_page } = useSelector(DeclinedAppointmentsSelector);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ sortBy: "declined_at", sortDirection: "desc" });
  const dispatch = useDispatch();

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(fetchDeclinedAppointments(1, { ...filtration, sortBy, sortDirection: dir, search: searchTerm }));
    };
    return (
      <tr>
        <th>#</th>
        <th>
          <TableHeaderItem title="Timestamp" sortBy="created_at" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Job No.#" sortBy="appid" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>Person Completing the Form</th>
        <th>Site/Department/ Location</th>
        <th>Format for Meeting</th>
        <th>Type of Meeting</th>
        <th>Meeting Date</th>
        <th>Meeting Start Time</th>
        <th>Meeting End Time</th>
        <th>Meeting Location</th>
        <th>Interpreter should report to whom?</th>
        <th>Name of the assigned interpreter</th>
        <th>Student's Name</th>
        <th>School Student Currently Attends</th>
        <th>Parent or Guardian's Name</th>
        <th>Parent or Guardian's Phone Number</th>
        <th>
          <TableHeaderItem title="Language" sortBy="language" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Current Status" sortBy="status" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>
          <TableHeaderItem title="Declined On" sortBy="declined_at" sortDirection={sortConfig.sortDirection} activeSortBy={sortConfig.sortBy} callBack={callBack} />
        </th>
        <th>Decline Reason</th>
      </tr>
    );
  }, [sortConfig, searchTerm, filtration]);

  const renderTableContent = React.useCallback(() => {
    if (!data || data.length === 0) {
      return (
        <tr>
          <td colSpan={21} style={{ textAlign: "center", padding: "30px 0" }}>No declined appointments found.</td>
        </tr>
      );
    }
    return data.map((item) => {
      const status = item.current_status;
      const bg = getBackgroundStyle(status);
      const eye = getEyeStyle(status);
      return (
        <tr key={item.id}>
          <td className="p-0" style={bg}>
            <div className="eye_area" style={eye}>
              <Link to={`/interpreter/appointments/view/${item.appointment_id}`}>
                <i className="fa-solid fa-eye"></i>
              </Link>
            </div>
          </td>
          <td style={bg}>
            {item.created_at ? moment(item.created_at).format("MMM D, YYYY HH:mm") : "—"}
          </td>
          <td style={bg}>
            <Link className="link_preview" to={`/interpreter/appointments/view/${item.appointment_id}`}>
              {item.appid || "N/A"}
            </Link>
          </td>
          <td style={bg}>{item.contact_person_name || "—"}</td>
          <td style={bg}>{item.school || "—"}</td>
          <td style={bg}>{item.meeting_format || "—"}</td>
          <td style={bg}>{item.meeting_type || "—"}</td>
          <td style={bg}>{item.date ? moment(item.date).format("MMM D, YYYY") : "—"}</td>
          <td style={bg}>{item.start_time ? moment(item.start_time, "HH:mm:ss").format("h:mm A") : "—"}</td>
          <td style={bg}>{item.end_time ? moment(item.end_time, "HH:mm:ss").format("h:mm A") : "—"}</td>
          <td style={bg}>{item.room_number_at_site || "—"}</td>
          <td style={bg}>{item.reporting_person || "—"}</td>
          <td style={bg}>{item.current_interpreter || "—"}</td>
          <td style={bg}>{item.student_name || "—"}</td>
          <td style={bg}>{item.school_student_attends || "—"}</td>
          <td style={bg}>{item.parent_name || "—"}</td>
          <td style={bg}>{item.parent_contact_number || "—"}</td>
          <td style={bg}>{item.language || "N/A"}</td>
          <td style={bg}>{status || "N/A"}</td>
          <td style={bg}>{item.declined_at ? moment(item.declined_at).format("MMM D, YYYY HH:mm") : "N/A"}</td>
          <td style={{ ...bg, maxWidth: "220px", wordBreak: "break-word" }}>{item.decline_reason || "N/A"}</td>
        </tr>
      );
    });
  }, [data]);

  return (
    <div className="mt-4">
      <ListLayout
        key={JSON.stringify(filtration)}
        time={false}
        fullMainHead="ms-3"
        title=""
        renderTableHeader={renderTableHeader}
        renderTableContent={renderTableContent}
        fetchList={fetchDeclinedAppointments}
        filtration={filtration}
        selectedItems={[]}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchPlaceholder="Search in this table ..."
        addButtonDisabled={true}
        totalPages={last_page}
        noOfItems={total}
        sortConfig={sortConfig}
        hidePagination={false}
        hideHeader={false}
      />
    </div>
  );
};

// ─── Page component ──────────────────────────────────────────────────────────

function DeclinedAppointmentList() {
  const dispatch = useDispatch();
  const { data, loader } = useSelector(DeclinedAppointmentsSelector);

  const defaultValues = { start_date: null, end_date: null, appointment_id: "", student_name: "", requester: "" };
  const { handleSubmit, formState: { errors }, control, reset, getValues, clearErrors } = useForm({ defaultValues });

  const baseParams = { pageLength: 20, sortDirection: "desc" };
  const [filters, setFilters] = useState(baseParams);

  useEffect(() => {
    dispatch(fetchDeclinedAppointments(1, baseParams));
  }, [dispatch]);

  const buildParams = (formValues) => ({
    ...baseParams,
    start_date: formValues.start_date ? moment(formValues.start_date).format("YYYY-MM-DD") : undefined,
    end_date: formValues.end_date ? moment(formValues.end_date).format("YYYY-MM-DD") : undefined,
    appointment_id: formValues.appointment_id?.trim() || undefined,
    student_name: formValues.student_name?.trim() || undefined,
    requester: formValues.requester?.trim() || undefined,
  });

  const onSubmit = (values) => {
    clearErrors();
    const params = buildParams(values);
    setFilters(params);
    dispatch(fetchDeclinedAppointments(1, params));
  };

  const handleResetFields = () => {
    reset(defaultValues);
    setFilters(baseParams);
    dispatch(fetchDeclinedAppointments(1, baseParams));
    clearErrors();
  };

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Declined Appointments</h2>
        </div>

        <div className="card_table">
          <form className="search_form_ad" onSubmit={handleSubmit(onSubmit)}>
            <div className="row-primary dahboard_filter appointments">
              <DateRangeFilter control={control} errors={errors} />

              <div className="row-primary">
                <h2>Appointment ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter ID" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Student Name</h2>
                <Controller
                  name="student_name"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter Student Name" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Requester</h2>
                <Controller
                  name="requester"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter Requester" />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between btns_area">
              <div className="row-primary row_10">
                <SubmitButton buttonType="submit" btnText="Search" className="green" />
                <SubmitButton buttonType="button" className="lightBlue" btnText="Reset" handleSubmit={handleResetFields} />
              </div>
            </div>
          </form>

          <DeclinedListTable data={data} filtration={filters} />
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default DeclinedAppointmentList;
